import { gql } from "@apollo/client";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { PartialDeep } from "type-fest";
import { SortEnumType, WalletDatalistPageQuery, WalletDatalistPageQueryVariables, WalletFilterInput, WalletSortInput } from "types/graphql";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import * as lodash from 'lodash';
import BooleanView from "components/common/views/BooleanView";
import { faArchive } from "@fortawesome/free-solid-svg-icons";
import { uuid } from "config/app-constants";

type ListItemType = NonNullable<WalletDatalistPageQuery['wallets']>['items'][0];

const query = gql`
query WalletDatalistPage($where: WalletFilterInput, $sort: [WalletSortInput!], $take: Int, $skip: Int) {
    wallets{
        items (where: $where, order: $sort, skip: $skip, take: $take){
            person {
                name
            }
            id,
            name,
            balance,
            archived
        }
        count(where: $where)
    }
}
`;

export type WalletDatalistProps = {
    name: string,
    fixedFilters?: WalletFilterInput
    config?: PartialDeep<DatalistPropsType<ListItemType, WalletFilterInput, WalletSortInput>>
}

const WalletDatalist = (props: WalletDatalistProps) => {
    const context = useAppContext();
    
    var datalistConfig: DatalistPropsType<ListItemType, WalletFilterInput, WalletSortInput> = {
        defaultView: 'table',
        dataSource: {
            async request(params) {
                try {
                    const variables: WalletDatalistPageQueryVariables = {
                        where: params.filters,
                        sort: params.sort,
                        take: params.take,
                        skip: params.skip
                    };
    
                    if (params.searchText) {
                        const search: WalletFilterInput = {
                            or: [
                                {
                                    normalizedName: {
                                        contains: params.searchText
                                    }
                                },
                                {
                                    name: {
                                        contains: params.searchText
                                    }
                                },
                                {
                                    person: {
                                        normalizedName: {
                                            contains: params.searchText
                                        }
                                    }
                                }
                            ]
                        }
    
                        variables.where = appendFilters(variables.where, search);
                    }

                    if (props.fixedFilters) {
                        variables.where = appendFilters(variables.where, props.fixedFilters);
                    }
    
                    const response = await graph<WalletDatalistPageQuery, WalletDatalistPageQueryVariables>({
                        query,
                        variables
                    });
    
                    if (response.errors) {
                        throw response.errors;
                    }
                    
                    return response.data!.wallets!;
                } catch (error: any) {
                    context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    id: uuid(),
                    name: "archived",
                    direction: SortEnumType.Asc
                }
            ]
        },
        name: props.name + "_wallet-datalist",
        itemId(i) {
            return i.id;
        },
        onItemClick: {
            link: {
                url: AppRoutes.PERSON_WALLET_VIEW,
                params: i => ({ id: i.id })
            }
        },
        table: {
            columns: [
                DatalistColumnDefinitions.personColumnDefinition(context),
                {
                    name: "name",
                    title: "fields.name.name",
                    sort: true,
                    filter: {
                        type: 'string'
                    }
                },
                {
                    name: "saleCount",
                    title: "sale count",
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    hide: true
                },
                {
                    name: "balance",
                    title: "fields.balance.name",
                    sort: true,
                    filter: {
                        type: 'number'
                    }
                },
                {
                    name: "archived",
                    title: "fields.archived.name",
                    sort: true,
                    filter: {
                        type: 'boolean'
                    },
                    type: 'boolean',
                    resolve(i) {
                        return i.archived ? <BooleanView value={i.archived} trueClassName="text-primary" tureIcon={faArchive} /> : "-"
                    },
                }
            ]
        }
    };

    const config = lodash.merge({}, datalistConfig, props.config);

    return <Datalist {...config} />;
};

export default WalletDatalist;
